import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowLeft, Loader2, Plus, Search, TriangleAlert, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../ui/button';
import { Switch } from '../../ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../ui/dialog';
import ContactFormFields, { EMPTY_CONTACT_FORM } from '../../contacts/ContactFormFields';
import { missingRequiredCustomFields } from '../../custom-fields/requiredFields';
import useCustomFields from '../../../hooks/useCustomFields';
import { isValidEmail } from '../../../utils/emailValidation';
import { normalizeContactPayload } from '../../../utils/normalizeContactPayload';
import { contactExists, errorText } from '../../../lib/apiErrors';
import { contactSeedFromSearch } from '../../../lib/contactSeed';
import { eventEnrollmentOutcomeMessages } from '../../../lib/eventEnrollmentOutcomes';
import { newOperationId } from '../../../lib/eventOutreachActions';
import { MAX_IMPORT_ROWS, parseGuestsCsv, skipReasonText } from '../../../lib/eventGuestsImport';

const START_CHOICES = [
    ['to_invite', 'To invite', 'Nothing sent yet'],
    ['invited', 'Invited', 'Already invited outside Nurture'],
    ['going', 'Going', 'Already confirmed — adds them to the guest list'],
];
const SCOPES = [['mine', 'My contacts'], ['all', 'All contacts'], ['tag', 'Tag'], ['list', 'List']];

const chip = (on) => `inline-flex h-7 items-center whitespace-nowrap rounded-lg border px-2.5 text-xs font-medium ${on ? 'border-stone-900 bg-stone-900 text-white' : 'border-stone-200 bg-white text-stone-700 hover:bg-stone-50'}`;

function StartIn({ value, onChange }) {
    return (
        <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Start in">
            <span className="text-xs font-semibold text-stone-500">Start in</span>
            {START_CHOICES.map(([key, label, tip]) => (
                <button key={key} type="button" title={tip} aria-pressed={value === key} data-testid={`add-guests-start-${key}`} className={chip(value === key)} onClick={() => onChange(key)}>{label}</button>
            ))}
        </div>
    );
}

/**
 * Add guests (#1013): ONE dialog for existing contacts and for new ones. The pick step searches contacts
 * (scopes My contacts / All / Tag / List), marks "Already a guest · <column>" and "Unowned · you become
 * owner", and adds them starting in To invite / Invited / Going. "Create new contact" swaps to the create step,
 * which renders the contact form INLINE (no second dialog, nobody leaves the page): the contact is created
 * through `POST /contacts` (dedupe, plan limit, audit, webhook), then added in one step. From the event's
 * start on, "Checked in now (walk-in)" is on by default and lands them in Attended with a Walk-in badge;
 * before it the Start in choice shows instead. A duplicate email offers "Add existing contact".
 * "Import CSV" (D12) is a third path: the browser parses the file, one request matches each row to a contact by
 * email (missing ones are created), and a summary lists who was added, created, already a guest or skipped.
 */
export default function AddGuestsDialog({
    open, onClose, api, eventId, eventLine, phase, guestLabels = {}, canCreateContact = false, canAssignOwner = false,
    currentUserName, initialMode = 'pick', onAdded,
}) {
    const started = phase !== 'before';
    const { fields: customFieldDefs } = useCustomFields('contact');
    const [step, setStep] = useState('pick');
    const [fromCheckIn, setFromCheckIn] = useState(false);
    const [query, setQuery] = useState('');
    const [scope, setScope] = useState('mine');
    const [scopeValue, setScopeValue] = useState('');
    const [tags, setTags] = useState([]);
    const [lists, setLists] = useState([]);
    const [results, setResults] = useState([]);
    const [onEvent, setOnEvent] = useState({});
    const [searching, setSearching] = useState(false);
    const [picked, setPicked] = useState([]);
    const [start, setStart] = useState('to_invite');
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState(EMPTY_CONTACT_FORM);
    const [walk, setWalk] = useState(false);
    const [exists, setExists] = useState(null);
    const [showRequired, setShowRequired] = useState(false);
    const [csv, setCsv] = useState(null);
    const [csvResult, setCsvResult] = useState(null);
    const [csvError, setCsvError] = useState('');
    const requestRef = useRef(0);
    const csvOperation = useRef(null);

    // A fresh dialog each time it opens; "Add walk-in" from Check-in opens straight on the create step.
    useEffect(() => {
        if (!open) return;
        setQuery(''); setScope('mine'); setScopeValue(''); setResults([]); setOnEvent({}); setPicked([]); setStart('to_invite');
        setForm(EMPTY_CONTACT_FORM); setExists(null); setShowRequired(false); setBusy(false);
        setCsv(null); setCsvResult(null); setCsvError(''); csvOperation.current = null;
        if (initialMode === 'walk-in') { setStep('create'); setFromCheckIn(true); setWalk(true); } else { setStep('pick'); setFromCheckIn(false); setWalk(false); }
    }, [open, initialMode]);

    useEffect(() => {
        if (!open) return;
        api.listContactTags().then((res) => setTags(Array.isArray(res) ? res : (res?.data || []))).catch(() => setTags([]));
    }, [open, api]);

    const search = useCallback(async () => {
        const token = ++requestRef.current;
        if (query.trim() && query.trim().length < 2) { setResults([]); setSearching(false); return; }
        setSearching(true);
        try {
            const params = { page_size: 50, search: query.trim() || undefined };
            if (scope === 'mine') params.owner_id = 'me';
            if (scope === 'tag' && scopeValue) params.tag = scopeValue;
            if (scope === 'list' && scopeValue) params.list_name = scopeValue;
            const res = await api.searchContacts(params);
            const rows = Array.isArray(res) ? res : res?.data || [];
            let states = {};
            const ids = rows.map((row) => row.contact_id).filter(Boolean);
            if (ids.length) {
                try { states = Object.fromEntries(((await api.getContactStates(eventId, ids))?.data || []).map((row) => [row.contact_id, row])); } catch { states = {}; }
            }
            if (token !== requestRef.current) return;
            setOnEvent(states);
            setResults(rows);
            const discovered = rows.flatMap((row) => [].concat(row.list_name || row.list || row.lists || [])).filter(Boolean);
            setLists((previous) => Array.from(new Set([...previous, ...discovered])));
        } catch {
            if (token === requestRef.current) setResults([]);
        } finally {
            if (token === requestRef.current) setSearching(false);
        }
    }, [api, eventId, query, scope, scopeValue]);

    useEffect(() => {
        if (!open || step !== 'pick') return undefined;
        const timer = setTimeout(search, 300);
        return () => clearTimeout(timer);
    }, [open, step, search]);

    const guestLabel = (contactId) => guestLabels[contactId] || onEvent[contactId]?.journey_label || onEvent[contactId]?.stage_label
        || (onEvent[contactId] ? 'On this event' : null);
    const toggle = (contact) => {
        if (guestLabel(contact.contact_id)) return;
        setPicked((current) => (current.some((row) => row.contact_id === contact.contact_id) ? current.filter((row) => row.contact_id !== contact.contact_id) : [...current, contact]));
    };

    const finish = (res, contacts) => {
        if (Array.isArray(res?.outcomes) && res.outcomes.length) {
            eventEnrollmentOutcomeMessages(res.outcomes, contacts).forEach(({ tone, message }) => toast[tone](message));
        } else {
            const added = (res?.created ?? contacts.length) + (res?.restored || 0);
            if (added > 0) toast.success(`Added ${added} guest${added > 1 ? 's' : ''}`);
            if (res?.skipped) toast.info(`${res.skipped} already on this event`);
        }
    };

    const addPicked = async () => {
        if (!picked.length) return;
        setBusy(true);
        try {
            const res = await api.addGuests(eventId, {
                contact_ids: picked.map((row) => row.contact_id), start, include_outcomes: true,
                assign_to_self: canAssignOwner && picked.some((row) => !row.owner_id),
                operation_id: newOperationId('add-guests', eventId),
            });
            finish(res, picked);
            await onAdded?.();
            onClose();
        } catch (err) {
            toast.error(errorText(err, 'Failed to add guests'));
        } finally { setBusy(false); }
    };

    const walkIn = started && walk;
    const addToEvent = async (contact) => {
        const res = await api.addGuests(eventId, {
            contact_ids: [contact.contact_id], start: walkIn ? 'attended' : start, ...(walkIn ? { walk_in: true } : {}),
            include_outcomes: true, assign_to_self: false, operation_id: newOperationId(walkIn ? 'walk-in' : 'add-guests', eventId),
        });
        finish(res, [contact]);
    };

    const createAndAdd = async () => {
        if (!form.first_name?.trim() || !form.last_name?.trim()) return toast.error('First and last name required');
        if (!form.email?.trim()) return toast.error('Email is required');
        if (!isValidEmail(form.email)) return toast.error('Please enter a valid email address');
        if (Object.keys(missingRequiredCustomFields(customFieldDefs, form.custom_fields)).length) { setShowRequired(true); return toast.error('Fill in the required custom fields'); }
        setBusy(true);
        let contact;
        try {
            contact = await api.createContact(normalizeContactPayload(form));
        } catch (err) {
            const dup = contactExists(err);
            if (dup) setExists(dup); else toast.error(errorText(err, 'Failed to save contact'));
            setBusy(false);
            return;
        }
        try {
            toast.success('Contact created');
            await addToEvent(contact);
            await onAdded?.();
            onClose();
        } catch (err) {
            // The contact is safe; only the event step failed.
            toast.error(errorText(err, 'Failed to add the contact to this event'));
            toast.info('The contact was created but could not be added to this event. Add them from "Add guests".');
        } finally { setBusy(false); }
    };

    const addExisting = async () => {
        if (!exists?.id) return;
        setBusy(true);
        try {
            await addToEvent({ contact_id: exists.id, first_name: exists.name || '', last_name: '' });
            await onAdded?.();
            onClose();
        } catch (err) {
            toast.error(errorText(err, 'Failed to add guests'));
        } finally { setBusy(false); }
    };

    const openCreate = () => {
        const seed = contactSeedFromSearch(query);
        setForm({ ...EMPTY_CONTACT_FORM, ...(seed || {}) });
        setExists(null); setShowRequired(false);
        setWalk(started);
        setStep('create');
    };

    const openImport = () => { setCsv(null); setCsvResult(null); setCsvError(''); csvOperation.current = null; setStep('import'); };
    const chooseCsv = async (event) => {
        const file = event.target.files?.[0];
        csvOperation.current = null;
        setCsv(null); setCsvResult(null); setCsvError('');
        if (!file) return;
        let text;
        try { text = await file.text(); } catch { setCsvError('This file could not be read.'); return; }
        const parsed = parseGuestsCsv(text);
        if (parsed.error) setCsvError(parsed.error); else setCsv({ ...parsed, name: file.name });
    };
    const changeStart = (next) => { csvOperation.current = null; setStart(next); };
    const importCsv = async () => {
        if (!csv || !csv.valid) return;
        setBusy(true); setCsvError('');
        // One key per file and starting column: a retry after a dropped connection replays instead of repeating.
        if (!csvOperation.current) csvOperation.current = newOperationId('import-guests', eventId);
        try {
            const res = await api.importGuests(eventId, { rows: csv.rows, start, operation_id: csvOperation.current });
            // The server numbers the rows it was sent; show the row the file has.
            setCsvResult({ ...res, skipped: (res?.skipped || []).map((item) => ({ ...item, row: csv.fileRows[item.row - 1] ?? item.row })) });
            await onAdded?.();
        } catch (err) {
            setCsvError(errorText(err, 'Failed to import guests'));
        } finally { setBusy(false); }
    };

    const createLabel = query.trim() ? `Create “${query.trim()}” as a new contact` : 'Create new contact';
    const title = step === 'create' ? (fromCheckIn ? 'Add walk-in' : 'New contact') : step === 'import' ? 'Import guests from CSV' : 'Add guests';
    return (
        <Dialog open={open} onOpenChange={(next) => { if (!next && !busy) onClose(); }}>
            <DialogContent data-testid="add-guests-dialog" className="sm:max-w-[620px]">
                <DialogHeader>
                    <div className="flex items-center gap-2">
                        {(step === 'import' || (step === 'create' && !fromCheckIn)) && (
                            <button type="button" aria-label="Back to search" data-testid="add-guests-back" disabled={busy} onClick={() => setStep('pick')} className="rounded-md p-1 text-stone-500 hover:bg-stone-100"><ArrowLeft className="h-4 w-4" aria-hidden="true" /></button>
                        )}
                        <DialogTitle>{title}</DialogTitle>
                    </div>
                    <DialogDescription>{step === 'create' ? `Saved to Contacts and added to ${eventLine}` : step === 'import' ? `Matched to Contacts by email and added to ${eventLine}` : eventLine}</DialogDescription>
                </DialogHeader>

                {step === 'pick' && (
                    <div className="min-w-0 space-y-3" data-testid="add-guests-pick">
                        <label className="relative block">
                            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" aria-hidden="true" />
                            <input aria-label="Search contacts by name, email or company" placeholder="Search contacts by name, email or company" autoFocus value={query} onChange={(e) => setQuery(e.target.value)}
                                className="h-10 w-full rounded-lg border border-stone-200 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                        </label>
                        <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Show">
                            {SCOPES.map(([key, label]) => <button key={key} type="button" aria-pressed={scope === key} data-testid={`add-guests-scope-${key}`} className={chip(scope === key)} onClick={() => { setScope(key); setScopeValue(''); }}>{label}</button>)}
                            {scope === 'tag' && (
                                <select aria-label="Contact tag" value={scopeValue} onChange={(e) => setScopeValue(e.target.value)} className="h-7 rounded border border-stone-200 px-2 text-xs">
                                    <option value="">Choose a tag</option>
                                    {tags.map((tag) => { const value = tag.tag || tag.name; return value ? <option key={value} value={value}>{value}</option> : null; })}
                                </select>
                            )}
                            {scope === 'list' && (
                                <select aria-label="Contact list" value={scopeValue} onChange={(e) => setScopeValue(e.target.value)} className="h-7 rounded border border-stone-200 px-2 text-xs">
                                    <option value="">Choose a list</option>
                                    {lists.map((value) => <option key={value} value={value}>{value}</option>)}
                                </select>
                            )}
                        </div>
                        <div className="max-h-72 divide-y divide-stone-100 overflow-y-auto rounded-lg border border-stone-100" data-testid="add-guests-results">
                            {searching && <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-stone-400" aria-label="Searching" /></div>}
                            {!searching && results.length === 0 && <p className="py-6 text-center text-sm text-stone-400">{query.trim().length >= 2 ? 'No contacts found' : 'No contacts match this view.'}</p>}
                            {!searching && results.map((contact) => {
                                const label = guestLabel(contact.contact_id);
                                const isPicked = picked.some((row) => row.contact_id === contact.contact_id);
                                const name = `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || contact.email;
                                return (
                                    <button key={contact.contact_id} type="button" data-testid={`add-guest-row-${contact.contact_id}`} disabled={Boolean(label)} aria-disabled={Boolean(label)}
                                        aria-pressed={isPicked} onClick={() => toggle(contact)}
                                        className={`flex w-full min-w-0 items-center gap-3 px-3 py-2.5 text-left ${label ? 'cursor-default opacity-75' : 'hover:bg-stone-50'} ${isPicked ? 'bg-stone-50' : ''}`}>
                                        <span className={`inline-flex h-4 w-4 shrink-0 items-center justify-center rounded border-[1.5px] text-[10px] text-white ${label ? 'border-stone-300 bg-stone-300' : isPicked ? 'border-stone-900 bg-stone-900' : 'border-stone-300 bg-white'}`} aria-hidden="true">{(isPicked || label) ? '✓' : ''}</span>
                                        <span className="min-w-0 flex-1">
                                            <span className="block truncate text-sm font-medium text-stone-900">{name}</span>
                                            <span className="block truncate text-xs text-stone-500">{[contact.title, contact.company || contact.account_name, contact.email].filter(Boolean).join(' · ')}</span>
                                        </span>
                                        {label ? <span className="shrink-0 rounded-md border border-stone-200 bg-stone-50 px-2 py-0.5 text-[11.5px] font-medium text-stone-700">{`Already a guest · ${label}`}</span>
                                            : !contact.owner_id ? <span className="shrink-0 rounded-md border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11.5px] font-medium text-amber-700">{canAssignOwner ? 'Unowned · you become owner' : 'Unowned'}</span> : null}
                                    </button>
                                );
                            })}
                            {canCreateContact && (
                                <button type="button" data-testid="add-guests-create-new" onClick={openCreate} className="flex w-full items-center gap-3 px-3 py-2.5 text-left hover:bg-stone-50">
                                    <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-stone-100 text-stone-600"><Plus className="h-3.5 w-3.5" aria-hidden="true" /></span>
                                    <span><span className="block text-sm font-medium text-stone-900">{createLabel}</span><span className="block text-xs text-stone-500">Opens the contact form here · the new contact is added to this event</span></span>
                                </button>
                            )}
                            {canCreateContact && (
                                <button type="button" data-testid="add-guests-import" onClick={openImport} className="flex w-full items-center gap-3 px-3 py-2.5 text-left hover:bg-stone-50">
                                    <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-stone-100 text-stone-600"><Upload className="h-3.5 w-3.5" aria-hidden="true" /></span>
                                    <span><span className="block text-sm font-medium text-stone-900">Import CSV</span><span className="block text-xs text-stone-500">Matched to contacts by email · people not in Contacts yet are created</span></span>
                                </button>
                            )}
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                            <StartIn value={start} onChange={setStart} />
                            <span className="flex-1" />
                            <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
                            <Button data-testid="add-guests-submit" onClick={addPicked} disabled={busy || picked.length === 0}>{picked.length ? `Add ${picked.length} ${picked.length === 1 ? 'guest' : 'guests'}` : 'Add guests'}</Button>
                        </div>
                    </div>
                )}

                {step === 'create' && (
                    <div className="min-w-0" data-testid="add-guests-create">
                        <ContactFormFields form={form} setForm={setForm} onEmailChange={() => setExists(null)} customFieldDefs={customFieldDefs} showRequired={showRequired} variant="compact" />
                        {exists && (
                            <div role="alert" data-testid="add-guests-exists" className="mt-4 flex items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                                <span className="flex items-center gap-2"><TriangleAlert className="h-4 w-4 shrink-0" aria-hidden="true" />{`${exists.name || 'A contact'} already has this email`}{exists.id ? ' · ' : ''}</span>
                                {exists.id && <button type="button" data-testid="add-guests-add-existing" disabled={busy} onClick={addExisting} className="shrink-0 font-semibold underline hover:no-underline">Add existing contact</button>}
                            </div>
                        )}
                        <div className="mt-4 space-y-2 rounded-lg border border-stone-200 p-3">
                            <span className="text-[11px] font-semibold uppercase tracking-wider text-stone-500">On this event</span>
                            {started && (
                                <label className="flex items-center gap-3 text-sm">
                                    <Switch aria-label="Checked in now (walk-in)" checked={walk} onCheckedChange={setWalk} data-testid="add-guests-walk-in" />
                                    <span><span className="block font-medium text-stone-900">Checked in now (walk-in)</span><span className="block text-xs text-stone-500">{walkIn ? 'Lands in Attended with a Walk-in badge' : 'Off · they start in the stage below'}</span></span>
                                </label>
                            )}
                            {!walkIn && <StartIn value={start} onChange={setStart} />}
                        </div>
                        <div className="mt-4 flex flex-wrap items-center gap-2">
                            <span className="flex-1 text-xs text-stone-500">{currentUserName ? `Owner: ${currentUserName} (you)` : 'You become the Contact Owner'}</span>
                            <Button variant="outline" onClick={() => (fromCheckIn ? onClose() : setStep('pick'))} disabled={busy}>Cancel</Button>
                            <Button data-testid="add-guests-create-submit" onClick={createAndAdd} disabled={busy}>{walkIn ? 'Create and check in' : 'Create and add'}</Button>
                        </div>
                    </div>
                )}

                {step === 'import' && (
                    <div className="min-w-0 space-y-3" data-testid="add-guests-import-step">
                        {!csvResult && (
                            <>
                                <label className="block space-y-1.5">
                                    <span className="text-xs font-semibold text-stone-500">CSV file</span>
                                    <input type="file" accept=".csv,text/csv" aria-label="CSV file" data-testid="add-guests-import-file" disabled={busy} onChange={chooseCsv}
                                        className="block w-full text-sm text-stone-700 file:mr-3 file:rounded-lg file:border file:border-stone-200 file:bg-white file:px-3 file:py-1.5 file:text-sm file:font-medium hover:file:bg-stone-50" />
                                </label>
                                <p className="text-xs text-stone-500">{`The first row is the header and needs an "email" column. first_name, last_name, company, title and phone are used when present; other columns are ignored. Up to ${MAX_IMPORT_ROWS} rows.`}</p>
                                {csv && (
                                    <p className="rounded-lg border border-stone-200 bg-stone-50 px-3 py-2 text-sm text-stone-800" data-testid="add-guests-import-preview">
                                        {`${csv.total} ${csv.total === 1 ? 'row' : 'rows'} found, ${csv.valid} with a valid email`}
                                    </p>
                                )}
                            </>
                        )}
                        {csvError && (
                            <div role="alert" data-testid="add-guests-import-error" className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                                <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" /><span>{csvError}</span>
                            </div>
                        )}
                        {csvResult && (
                            <div className="space-y-2" data-testid="add-guests-import-result" role="status">
                                <ul className="grid grid-cols-2 gap-2 text-sm">
                                    {[['Added', csvResult.added], ['Created as new contacts', csvResult.created], ['Already guests', csvResult.already_guests], ['Skipped', csvResult.skipped.length]].map(([label, count]) => (
                                        <li key={label} className="rounded-lg border border-stone-200 px-3 py-2"><span className="block text-lg font-semibold text-stone-900">{count}</span><span className="text-xs text-stone-500">{label}</span></li>
                                    ))}
                                </ul>
                                {csvResult.skipped.length > 0 && (
                                    <ul className="max-h-40 divide-y divide-stone-100 overflow-y-auto rounded-lg border border-stone-100 text-sm" data-testid="add-guests-import-skipped">
                                        {csvResult.skipped.map((item) => (
                                            <li key={`${item.row}-${item.reason}`} className="flex min-w-0 items-center gap-2 px-3 py-1.5">
                                                <span className="shrink-0 text-xs text-stone-500">{`Row ${item.row}`}</span>
                                                <span className="min-w-0 flex-1 truncate text-stone-800">{item.email || '(no email)'}</span>
                                                <span className="shrink-0 text-xs text-stone-500">{skipReasonText(item.reason)}</span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        )}
                        <div className="flex flex-wrap items-center gap-2">
                            {!csvResult && <StartIn value={start} onChange={changeStart} />}
                            <span className="flex-1" />
                            {csvResult ? (
                                <Button data-testid="add-guests-import-done" onClick={onClose}>Done</Button>
                            ) : (
                                <>
                                    <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
                                    <Button data-testid="add-guests-import-submit" onClick={importCsv} disabled={busy || !csv || !csv.valid}>
                                        {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden="true" />}
                                        {busy ? 'Importing…' : csv?.valid ? `Add ${csv.valid} ${csv.valid === 1 ? 'guest' : 'guests'}` : 'Import'}
                                    </Button>
                                </>
                            )}
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
